import { format } from 'date-fns'
import {
  type AccommodationDetail,
  type UnitAvailability,
  UnitAvailabilitySchema,
  UnitDetailSchema,
  type LocationSummary,
  LocationSummarySchema,
  type UnitPhotos,
  UnitPhotosSchema,
  type LocationSearchResult,
  type SimilarOffer,
  SimilarOfferSchema,
  type OrderSummaryRequest,
  type OrderSummaryResponse,
  OrderSummaryResponseSchema,
  SearchResultSchema,
  type OrderRequest,
  type Order,
  OrderSchema,
  type OrderDraftRequest,
  type OrderDraftResponse,
  OrderDraftResponseSchema,
  type CreateOrderResponse,
  CreateOrderResponseSchema,
  type EnumResponse,
  EnumResponseSchema,
  type OrderPaymentUrl,
  OrderPaymentUrlSchema,
  type LocationInquiryRequest,
  type LocationInquiryResponse,
  LocationInquiryResponseSchema,
  type LocationPriceReportRequest,
  type LocationPriceReportResponse,
  LocationPriceReportResponseSchema,
  type LocationRoute,
  LocationRouteSchema,
  type CatalogItem,
  CatalogItemSchema,
} from './types'
import { baseUrl, getJson, postJson } from '../http'
import { z } from 'zod'
import { readFbCookies } from '@/lib/track/shared/fbCookies'
import { readGaCookies } from '@/lib/track/shared/gaCookies'
import { readUtmParams } from '@/lib/track/shared/utmParams'
import type { LocationSearchParams } from '@/features/accommodation/hooks/useLocatiomSearchParams'

async function searchAccommodationFetch(
  { fromDate, toDate, labelId, beds, cribs, adult, pets, facility, skip, take, order, direction }: LocationSearchParams,
  signal: AbortSignal,
  regionId?: number,
  categoryIds?: string,
): Promise<LocationSearchResult[]> {
  const params = new URLSearchParams({
    scope: 'location.photos,location.sites,unit.photos',
    beds: beds.toString(),
    cribs: cribs.toString(),
    skip: skip.toString(),
    take: take.toString(),
    order,
    direction,
  })

  if (fromDate) {
    params.append('arrival', format(fromDate, 'yyyy-MM-dd'))
  }
  if (toDate) {
    params.append('departure', format(toDate, 'yyyy-MM-dd'))
  }
  if (regionId !== undefined) {
    params.append('regionId', regionId.toString())
  }
  if (categoryIds !== undefined) {
    params.append('categoryId', categoryIds)
  }
  if (labelId) {
    params.append('labelId', labelId.toString())
  }
  if (adult) {
    params.append('adult', 'true')
  }
  if (pets) {
    params.append('pets', 'true')
  }
  if (facility) {
    params.append('facility', facility)
  }

  const url = `${baseUrl}/offer?${params.toString()}`
  return getJson(url, z.array(SearchResultSchema), { signal })
}

async function similarOffersFetch(
  { fromDate, toDate, labelId, beds, cribs, adult, pets, facility }: LocationSearchParams,
  signal: AbortSignal,
  regionId?: number,
  categoryIds?: string,
): Promise<SimilarOffer[]> {
  const params = new URLSearchParams({
    scope: 'location.photos,location.sites,unit.photos',
    beds: beds.toString(),
    cribs: cribs.toString(),
  })

  if (fromDate) {
    params.append('arrival', format(fromDate, 'yyyy-MM-dd'))
  }
  if (toDate) {
    params.append('departure', format(toDate, 'yyyy-MM-dd'))
  }
  if (regionId !== undefined) {
    params.append('regionId', regionId.toString())
  }
  if (categoryIds !== undefined) {
    params.append('categoryId', categoryIds)
  }
  if (labelId) {
    params.append('labelId', labelId.toString())
  }
  if (adult) {
    params.append('adult', 'true')
  }
  if (pets) {
    params.append('pets', 'true')
  }
  if (facility) {
    params.append('facility', facility)
  }

  const url = `${baseUrl}/offer/similar?${params.toString()}`
  return getJson(url, z.array(SimilarOfferSchema), { signal })
}

async function offerDetailFetch(offerId: string, signal: AbortSignal): Promise<LocationSearchResult> {
  const url = `${baseUrl}/offer/${offerId}?scope=location.photos,location.sites,unit.photos`
  return getJson(url, SearchResultSchema, { signal })
}

async function accommodationDetailFetch(
  locationSlug: string,
  unitSlug: string,
  signal: AbortSignal,
): Promise<AccommodationDetail> {
  const scope = [
    'facility',
    'spaces',
    'photos',
    'siblings',
    'offers',
    'ogImage',
    ...['photos', 'facility', 'surcharges', 'surchargePayments', 'business', 'trips', 'sites', 'labels', 'ogImage'].map(
      (include) => `location.${include}`,
    ),
  ].join(',')
  const url = `${baseUrl}/location/${locationSlug}/unit/${unitSlug}?scope=${scope}`
  // The API roots the response at the unit; the pages mostly render the place,
  // so the location becomes the root and the unit hangs off it.
  const { location, ...unit } = await getJson(url, UnitDetailSchema, { signal })
  return { ...location, unit }
}

async function locationSummaryFetch(slugOrId: string, signal: AbortSignal): Promise<LocationSummary> {
  const url = `${baseUrl}/location/${slugOrId}?scope=photos,units,units.photos`
  return getJson(url, LocationSummarySchema, { signal })
}

async function unitPhotosFetch(unitId: number, signal: AbortSignal): Promise<UnitPhotos> {
  const url = `${baseUrl}/unit/${unitId}?scope=photos`
  return getJson(url, UnitPhotosSchema, { signal })
}

async function unitAvailabilityFetch(unitId: number, signal: AbortSignal): Promise<UnitAvailability> {
  const url = `${baseUrl}/unit/${unitId}/arrival?scope=departures`
  return getJson(url, UnitAvailabilitySchema, { signal })
}

async function getOrderSummaryFetch(order: OrderSummaryRequest, signal: AbortSignal): Promise<OrderSummaryResponse> {
  const url = `${baseUrl}/summary`
  return postJson(
    url,
    {
      ...order,
      start: format(order.start, 'yyyy-MM-dd'),
      end: format(order.end, 'yyyy-MM-dd'),
    },
    OrderSummaryResponseSchema,
    { signal },
  )
}

async function enumsFetch(signal: AbortSignal): Promise<EnumResponse> {
  const url = `${baseUrl}/enum`
  return getJson(url, EnumResponseSchema, { signal })
}

async function createOrderFetch(order: OrderRequest, signal: AbortSignal): Promise<CreateOrderResponse> {
  const url = `${baseUrl}/order`
  // Merge Meta + GA cookies and UTM/gclid attribution so the backend can
  // forward them to the Conversions API / GA4 Measurement Protocol.
  return postJson(
    url,
    { ...order, ...readFbCookies(), ...readGaCookies(), ...readUtmParams() },
    CreateOrderResponseSchema,
    { signal },
  )
}

async function saveOrderDraftFetch(
  draft: OrderDraftRequest,
  options: { keepalive?: boolean } = {},
): Promise<OrderDraftResponse> {
  const url = `${baseUrl}/order-draft`
  return postJson(url, draft, OrderDraftResponseSchema, {
    keepalive: options.keepalive,
  })
}

async function getOrderFetch(orderNumber: string, signal: AbortSignal): Promise<Order> {
  const url = `${baseUrl}/order/${orderNumber}?scope=location,location.photos,location.surcharges,location.surchargePayments,location.business,unit,unit.photos`
  return getJson(url, OrderSchema, { signal })
}

async function getOrderPaymentUrlFetch(
  orderNumber: string,
  returnUrl: string,
  signal: AbortSignal,
): Promise<OrderPaymentUrl> {
  const url = `${baseUrl}/order/${orderNumber}/pay`
  return postJson(url, { returnUrl }, OrderPaymentUrlSchema, { signal })
}

// The customer withdraws a request its owner has not answered yet; the token
// comes in the link from the request confirmation e-mail.
async function cancelOrderRequestFetch(orderNumber: string, token: string, signal: AbortSignal): Promise<Order> {
  const url = `${baseUrl}/order/${orderNumber}/request/${token}/cancel`
  return postJson(url, {}, OrderSchema, { signal })
}

async function createLocationInquiryFetch(
  slug: string,
  inquiry: LocationInquiryRequest,
  signal?: AbortSignal,
): Promise<LocationInquiryResponse> {
  const url = `${baseUrl}/location/${slug}/inquiry`
  return postJson(url, inquiry, LocationInquiryResponseSchema, { signal })
}

async function createLocationPriceReportFetch(
  slug: string,
  report: LocationPriceReportRequest,
  signal?: AbortSignal,
): Promise<LocationPriceReportResponse> {
  const url = `${baseUrl}/location/${slug}/price-report`
  return postJson(url, report, LocationPriceReportResponseSchema, { signal })
}

async function catalogFetch(signal: AbortSignal): Promise<CatalogItem[]> {
  const url = `${baseUrl}/metadata?scope=location`
  return getJson(url, z.array(CatalogItemSchema), { signal })
}

async function locationRouteFetch(
  slug: string,
  latitude: number,
  longitude: number,
  signal: AbortSignal,
): Promise<LocationRoute> {
  const params = new URLSearchParams({
    latitude: latitude.toString(),
    longitude: longitude.toString(),
  })
  const url = `${baseUrl}/location/${slug}/route?${params.toString()}`
  return getJson(url, LocationRouteSchema, { signal })
}

export default {
  searchLocationsFetch: searchAccommodationFetch,
  similarOffersFetch,
  offerDetailFetch,
  accommodationDetailFetch,
  locationSummaryFetch,
  unitPhotosFetch,
  enumsFetch,
  unitAvailabilityFetch,
  getOrderSummaryFetch,
  createOrderFetch,
  saveOrderDraftFetch,
  getOrderFetch,
  getOrderPaymentUrlFetch,
  cancelOrderRequestFetch,
  createLocationInquiryFetch,
  createLocationPriceReportFetch,
  locationRouteFetch,
  catalogFetch,
}
